import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Search, ChevronRight, Loader2, Ban, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", business_name: "", upi_id: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", business_name: "", upi_id: "" });
      load();
    } catch (err) {
      toast.error(apiErr(err, "Could not create user"));
    } finally {
      setLoading(false);
    }
  };

  const filtered = users.filter((u) => `${u.name} ${u.email} ${u.business_name}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Users</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-blue-600 hover:bg-blue-700" data-testid="create-user-btn"><UserPlus className="h-4 w-4 mr-2" /> New user</Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Create merchant user</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cu-name" /></div>
                <div className="space-y-1.5 col-span-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="cu-email" /></div>
                <div className="space-y-1.5 col-span-2"><Label>Password</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="cu-password" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cu-phone" /></div>
                <div className="space-y-1.5"><Label>Business</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="cu-business" /></div>
                <div className="space-y-1.5 col-span-2"><Label>UPI ID (optional)</Label><Input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} placeholder="name@bank" data-testid="cu-upi" /></div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 w-full rounded-xl" data-testid="cu-submit">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create user"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-9 h-11 bg-white dark:bg-slate-900" data-testid="user-search-input" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {filtered.map((u) => (
          <button key={u.id} onClick={() => navigate(`/admin/users/${u.id}`)} data-testid={`user-card-${u.id}`}
            className="text-left rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 hover:border-blue-400 hover:shadow-md transition-all flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-blue-600/10 text-blue-600 grid place-items-center font-heading font-bold shrink-0">{u.name?.[0]?.toUpperCase() || "U"}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-slate-900 dark:text-white truncate">{u.name}</span>
                {u.blocked ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-600 flex items-center gap-1"><Ban className="h-3 w-3" />Blocked</span>
                  : <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Active</span>}
              </div>
              <div className="text-xs text-slate-500 truncate">{u.email}</div>
              <div className="text-xs font-mono-data text-slate-600 dark:text-slate-400 mt-1">Coll {fmtMoney(u.collection_balance)} • Pay {fmtMoney(u.payout_balance)}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
          </button>
        ))}
        {filtered.length === 0 && <div className="col-span-2 py-10 text-center text-slate-400 text-sm">No users found</div>}
      </div>
    </div>
  );
}
